import { useEffect, useRef, useState } from "react";
import homeService from "../services/home.service";
import { BannerImageResponse } from "../models/response/bannerImageResponse";
import { ActiveVerticalResponse } from "../models/response/activeVerticalResponse";
import { Header1 } from "../../../components/ui";
import { DeviceType } from "../../../models/constants/deviceType";
import {
  SnapCarousel,
  DraggableCarousel,
} from "../../../components/ui/carousel";
import { BetHistory, RecentPlayed, SportsWidget, TrendingGames } from "./index";
import localization from "../../../config/localization";
import { useNavigate } from "react-router-dom";
import { Games } from "./games/Games";
import { localStorageService } from "../../../services";
import { BASE_URL } from "../../../config/appConfig";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import { useAppDispatch } from "../../../store/hooks";
import { GameVertical } from "../../../models/constants/gameVertical";

const SPORTS_VERTICAL = GameVertical.Sports.toLowerCase();
const SAP_VERTICAL = GameVertical.SAP.toLowerCase();

const filterActiveVerticalsForNav = (
  details: ActiveVerticalResponse["details"],
  userVerticalsCsv: string | null | undefined,
) => {
  const userVerticalsList = (userVerticalsCsv ?? "")
    .toLowerCase()
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return details.filter((vertical) => {
    const nameLower = vertical.name.toLowerCase();
    const isSportsIndia =
      nameLower === GameVertical.SportsIndia.toLowerCase() ||
      nameLower === "sports india";

    if (nameLower === SPORTS_VERTICAL) {
      return !userVerticalsList.includes(SPORTS_VERTICAL);
    }
    if (isSportsIndia) {
      return !userVerticalsList.includes(SAP_VERTICAL);
    }
    return !userVerticalsList.some((x) => x === nameLower);
  });
};

export default function Home() {
  const [casinoBanners, setBanners] = useState<BannerImageResponse>();
  const [homeBanners, setHomeBanners] = useState<BannerImageResponse>();
  const [verticals, setVerticals] = useState<ActiveVerticalResponse>();
  const [deviceType, setDeviceType] = useState<string>(DeviceType.Web);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [width, setWidth] = useState("100%");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setIsAuthenticated(localStorageService.getItem("isUserLoggedIn") === true);
  }, []);

  const calculateWidth = () => {
    const w = window.innerWidth;

    if (w >= 1024) {
      setWidth("90%");
    } else if (w >= 768) {
      setWidth("70%");
    } else {
      setWidth("80%");
    }
  };

  useEffect(() => {
    calculateWidth();
  }, []);

  useEffect(() => {
    window.addEventListener("resize", calculateWidth);
    return () => window.removeEventListener("resize", calculateWidth);
  }, []);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setDeviceType(width < 768 ? DeviceType.Mobile : DeviceType.Web);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  useEffect(() => {
    const fetchCasinoBanners = async () => {
      try {
        dispatch(changeLoaderState(true));
        const response = await homeService.retrieveBanners({
          bannerType: "Casino HomePage",
          deviceType: "Web",
          language: "English",
        });

        if (response.isSuccess && response.data?.details?.length) {
          const banners = response.data;

          await Promise.all(
            banners.details.map((banner) => {
              return new Promise((resolve) => {
                const image = new Image();

                image.src = `data:image/png;base64,${banner.banner}`;
                image.onload = () => resolve(true);
                image.onerror = () => resolve(false);
              });
            }),
          );

          setBanners(response.data ?? undefined);
        }
      } finally {
        dispatch(changeLoaderState(false));
      }
    };

    const fetchMobileBanners = async () => {
      try {
        dispatch(changeLoaderState(true));
        var response = await homeService.retrieveBanners({
          bannerType: "HomePage",
          deviceType: deviceType,
          language: "English",
        });
        if (response.isSuccess) {
          setHomeBanners(response.data ?? undefined);
        }
      } finally {
        dispatch(changeLoaderState(false));
      }
    };

    const fetchActiveVerticals = async () => {
      try {
        dispatch(changeLoaderState(true));
        const response = await homeService.fetchActiveVerticals();
        if (!response.isSuccess || !response.data?.details) {
          setVerticals(undefined);
          return;
        }

        let details = response.data.details;
        if (isAuthenticated) {
          const userVerticals = await homeService.fetchUserVerticals();
          if (userVerticals.isSuccess && userVerticals.data) {
            details = filterActiveVerticalsForNav(
              details,
              userVerticals.data.verticalsCsv,
            );
          }
        }

        setVerticals({ details });
      } finally {
        dispatch(changeLoaderState(false));
      }
    };

    fetchCasinoBanners();
    fetchMobileBanners();
    fetchActiveVerticals();
  }, [deviceType, isAuthenticated, dispatch]);

  const getBannerTitle = (verticalName: string) => {
    switch (verticalName) {
      case "Sports":
        return "SportsBook";
      default:
        return verticalName;
    }
  };

  const payViaImages = [
    "/icons/ic-paytm.svg",
    "/icons/ic-upi.svg",
    "/icons/ic-phonePe.svg",
    "/icons/ic-crypto.svg",
  ];

  const handleNavigateVertical = (verticalName: string) => {
    switch (verticalName) {
      case "sports":
        navigate("/sportsbookplus");
        break;
      case "sports-india":
        navigate("/bharatsportsbook");
        break;

      case "exchange":
        isAuthenticated
          ? navigate("/exchange/Wickets9")
          : navigate("/exchange");
        if (isAuthenticated) {
          navigate("/exchange/Wickets9");
        } else {
          navigate("/exchange");
        }
        break;

      default:
        navigate(`/${verticalName.toLowerCase()}`);
    }
  };

  const handleNavigateBanner = (bannerLink: string | null) => {
    if (bannerLink && bannerLink !== "undefined") {
      if (bannerLink.toString().includes(BASE_URL)) {
        const redirection = bannerLink.replace(
          BASE_URL,
          window.location.origin,
        );
        window.location.href = redirection;
      }
    }
  };

  const isDragging = useRef(false);
  const startX = useRef(0);
  const verticalCount = verticals?.details?.length ?? 0;

  return (
    <div>
      <div
        className={`grid grid-cols-1 gap-6 ${verticalCount > 0 ? "lg:grid-cols-2" : "lg:grid-cols-1"
          }`}
      >
        <div className="w-full overflow-hidden">
          <SnapCarousel
            startIndex={1}
            infinite
            showAdjacent={width < "90%"}
            centerMode={true}
            itemWidth={width}
          >
            {casinoBanners?.details?.map((banner) => (
              <div className="px-4 ">
                <div
                  className="rounded-xl h-44 sm:h-56 md:h-64 lg:h-80 
             cursor-grab bg-center bg-cover mx-auto w-full "
                  style={{
                    backgroundImage: `url(data:image/png;base64,${banner.banner})`,
                  }}
                  onMouseDown={(e) => {
                    isDragging.current = false;
                    startX.current = e.clientX;
                  }}
                  onMouseMove={(e) => {
                    if (Math.abs(e.clientX - startX.current) > 5) {
                      isDragging.current = true;
                    }
                  }}
                  onClick={(e) => {
                    if (isDragging.current) {
                      e.preventDefault();
                      return;
                    }
                    if (banner.bannerLink) {
                      handleNavigateBanner(banner.bannerLink);
                    }
                  }}
                ></div>
              </div>
            ))}
          </SnapCarousel>
        </div>

        {verticalCount > 0 && (
          <div className="lg:h-80">
            <div
              className={`grid gap-3 h-full ${verticalCount <= 2
                ? "grid-cols-1 lg:grid-rows-2"
                : "grid-cols-2"
                }`}
            >
              {verticals?.details?.map((vertical, index) => {
                const total = verticals.details.length;
                const isLast = index === total - 1;
                const isOdd = total % 2 !== 0;
                const isSingle = total === 1;
                const isTripleOddLast = total >= 3 && isOdd && isLast;

                const bannerImage = homeBanners?.details.find(
                  (x) => x.title === getBannerTitle(vertical.name),
                )?.banner;

                return (
                  <div
                    key={index}
                    onClick={() =>
                      handleNavigateVertical(vertical.name.toLowerCase())
                    }
                    className={`
            flex flex-col justify-end p-4
            rounded-xl cursor-pointer
            h-32 sm:h-36 md:h-28 lg:h-full
            bg-center bg-cover 
            transition-transform duration-300 hover:scale-[1.02]
            ${isSingle ? "lg:row-span-2" : ""}
            ${isTripleOddLast ? "col-span-2" : ""}
          `}
                    style={{
                      backgroundImage: `url(data:image/png;base64,${bannerImage})`,
                    }}
                  >
                    <Header1 className="text-base sm:text-lg md:text-base lg:text-lg">
                      {localization.getString(vertical.name.toLowerCase())}
                    </Header1>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {isAuthenticated && <RecentPlayed />}
      <SportsWidget />
      <Games />
      <TrendingGames />

      <div className="text-white bg-dark-gradient  rounded-xl mt-3">
        <div>
          <div className="flex items-center">
            <span className="mt-3 ml-3 text-xl font-semibold">
              {localization.pay_via}
            </span>
          </div>
        </div>

        <div className="mt-2 pb-4 px-4">
          <div className="flex justify-start">
            <DraggableCarousel gap="gap-8">
              {payViaImages.map((icon, index) => (
                <div key={index} className="shrink-0 flex rounded-lg">
                  <img
                    src={icon}
                    alt={`Pay via - ${index}`}
                    className="w-16 h-16 object-contain"
                  />
                </div>
              ))}
            </DraggableCarousel>
          </div>
        </div>
      </div>

      <BetHistory />
    </div>
  );
}
